import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Mail, Ticket, Check, X, Send, Eye, EyeOff, Rocket, Target, Users, Search, GraduationCap, Briefcase, Clock, TrendingUp, UserCog } from 'lucide-react';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { Logo } from '@/components/Logo';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { InviteSummary } from '@/components/auth/InviteSummary';

const emailSchema = z.string().email('Please enter a valid email address');
const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a digit')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain a symbol');

type AuthMode = 'signIn' | 'signUp' | 'resetPassword' | 'resetSent' | 'enterCode' | 'setPassword' | 'selectAccountType' | 'enterInviteCode' | 'verifyEmail';
type AccountType = 'student' | 'professional' | 'professor' | 'recruiter' | null;

// Account types that require an invite code
const RESTRICTED_ACCOUNT_TYPES: AccountType[] = ['student', 'professor', 'recruiter'];

const Auth = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>('signIn');
  const [accountType, setAccountType] = useState<AccountType>(null);
  const [selectedRoleLabel, setSelectedRoleLabel] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetCooldownEndsAt, setResetCooldownEndsAt] = useState<number | null>(null);
  const [resetCooldownSecondsLeft, setResetCooldownSecondsLeft] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [resetCodeVerifying, setResetCodeVerifying] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; confirmPassword?: string; fullName?: string; inviteCode?: string; studentEmail?: string; resetCode?: string; terms?: string }>({});
  
  // Terms acceptance state
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Invite code state
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeValidating, setInviteCodeValidating] = useState(false);
  const [inviteCodeValid, setInviteCodeValid] = useState<boolean | null>(null);
  const [inviteRole, setInviteRole] = useState<string | null>(null);
  const [inviteKind, setInviteKind] = useState<'role' | 'license' | null>(null);
  const [inviteLicenseName, setInviteLicenseName] = useState<string | null>(null);
  const [inviteSchoolName, setInviteSchoolName] = useState<string | null>(null);
  const [inviteLicenseType, setInviteLicenseType] = useState<string | null>(null);
  const [inviteSeatsAvailable, setInviteSeatsAvailable] = useState<number | null>(null);
  const [inviteMaxSeats, setInviteMaxSeats] = useState<number | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  
  // Student verification state
  const [isStudent, setIsStudent] = useState(false);
  const [studentEmail, setStudentEmail] = useState('');
  const [studentEmailSending, setStudentEmailSending] = useState(false);
  const [studentEmailSent, setStudentEmailSent] = useState(false);
  const [detectedSchool, setDetectedSchool] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeVerifying, setVerificationCodeVerifying] = useState(false);
  const [studentEmailVerified, setStudentEmailVerified] = useState(false);
  const [schoolPendingReview, setSchoolPendingReview] = useState(false);
  
  const [googleLoading, setGoogleLoading] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signIn, signUp, signInWithGoogle, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for password recovery event (when user clicks reset link in email)
  useEffect(() => {
    let cancelled = false;

    const cleanUrl = () => {
      // Remove auth params/hash after Supabase processes them
      window.history.replaceState(null, '', window.location.pathname);
    };

    const parseHashParams = () => {
      const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
      return new URLSearchParams(raw);
    };

    const initRecoveryFromUrl = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = parseHashParams();

      const error = searchParams.get('error') || hashParams.get('error');
      const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
      if (error) {
        toast({
          title: 'Reset link error',
          description: errorDescription || 'This reset link is invalid or has expired. Please request a new one.',
          variant: 'destructive',
        });
        cleanUrl();
        return;
      }

      // PKCE flow can return an auth code in the query string.
      const code = searchParams.get('code');
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          toast({
            title: 'Reset link expired',
            description: exchangeError.message || 'Please request a new reset link.',
            variant: 'destructive',
          });
          cleanUrl();
          return;
        }

        if (!cancelled) {
          setMode('setPassword');
          cleanUrl();
        }
        return;
      }

      // Implicit flow puts tokens in the hash along with type=recovery.
      const type = hashParams.get('type') || searchParams.get('type');
      if (type === 'recovery') {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          toast({
            title: 'Reset link expired',
            description: 'Please request a new password reset email and open the newest link.',
            variant: 'destructive',
          });
          cleanUrl();
          return;
        }

        if (!cancelled) {
          setMode('setPassword');
          cleanUrl();
        }
      }
    };

    initRecoveryFromUrl();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && !cancelled) {
        setMode('setPassword');
        cleanUrl();
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [toast]);

  useEffect(() => {
    // Only redirect if user is logged in AND not in setPassword mode
    if (user && mode !== 'setPassword') {
      // Honor ?next= (or location.state.from) so /join/CODE links resume after sign-in.
      // Sanitize: only allow same-origin paths starting with a single "/".
      let dest = '/dashboard';
      try {
        const params = new URLSearchParams(window.location.search);
        const nextParam = params.get('next');
        const fromState = (location.state as any)?.from;
        const fromPath = typeof fromState === 'string'
          ? fromState
          : fromState?.pathname
            ? `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`
            : null;
        const candidate = nextParam || fromPath;
        if (candidate && /^\/(?!\/)/.test(candidate)) {
          dest = candidate;
        }
      } catch {}
      navigate(dest, { replace: true });
    }
  }, [user, navigate, mode, location]);

  // Pre-fill invite code + email from query params (used after paid-seat checkout)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('invite');
      const fromStash = sessionStorage.getItem('pending_invite_code') || localStorage.getItem('pending_invite_code');
      const code = (fromUrl || fromStash || '').trim().toUpperCase();
      const forceSignup = params.get('signup') === '1';
      const prefillEmail = (params.get('email') || sessionStorage.getItem('pending_invite_buyer_email') || '').trim().toLowerCase();
      if (code && code.length >= 4 && !inviteCode) {
        setInviteCode(code);
        setShowInviteCode(true);
        setMode('signUp');
      }
      if (forceSignup) setMode('signUp');
      if (prefillEmail && !email) setEmail(prefillEmail);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent users from spamming reset emails (only the newest link works)
  useEffect(() => {
    if (!resetCooldownEndsAt) {
      setResetCooldownSecondsLeft(0);
      return;
    }

    const tick = () => {
      const remainingMs = resetCooldownEndsAt - Date.now();
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
      setResetCooldownSecondsLeft(remainingSec);
      if (remainingSec === 0) setResetCooldownEndsAt(null);
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [resetCooldownEndsAt]);

  useEffect(() => {
    const resetInviteMeta = () => {
      setInviteLicenseName(null);
      setInviteSchoolName(null);
      setInviteLicenseType(null);
      setInviteSeatsAvailable(null);
      setInviteMaxSeats(null);
      setInviteExpiresAt(null);
    };

    const validateInviteCode = async () => {
      if (!inviteCode || inviteCode.length < 4) {
        setInviteCodeValid(null);
        setInviteRole(null);
        setInviteKind(null);
        resetInviteMeta();
        return;
      }

      setInviteCodeValidating(true);
      try {
        const { data, error } = await supabase.functions.invoke('validate-invite-code', {
          body: { code: inviteCode }
        });

        if (error) throw error;

        if (data.valid) {
          setInviteCodeValid(true);
          setInviteRole(data.role);
          setInviteKind(data.kind ?? 'role');
          setInviteLicenseName(data.license_name ?? null);
          setInviteSchoolName(data.school_name ?? null);
          setInviteLicenseType(data.license_type ?? null);
          setInviteSeatsAvailable(
            typeof data.seats_available === 'number' ? data.seats_available : null
          );
          setInviteMaxSeats(typeof data.max_seats === 'number' ? data.max_seats : null);
          setInviteExpiresAt(data.expires_at ?? null);
        } else {
          setInviteCodeValid(false);
          setInviteRole(null);
          setInviteKind(null);
          resetInviteMeta();
          setErrors(prev => ({ ...prev, inviteCode: data.error || 'Invalid invite code' }));
        }
      } catch (error) {
        console.error('Error validating invite code:', error);
        setInviteCodeValid(false);
        setInviteRole(null);
        setInviteKind(null);
        resetInviteMeta();
      } finally {
        setInviteCodeValidating(false);
      }
    };

    const debounce = setTimeout(validateInviteCode, 500);
    return () => clearTimeout(debounce);
  }, [inviteCode]);

  // Check if the invite code role matches the selected account type
  const isInviteCodeRoleValid = (): boolean => {
    if (!inviteCodeValid || !inviteRole || !accountType) return false;

    // License-kind codes: student code satisfies student account; professor code satisfies professor account
    if (inviteKind === 'license') {
      if (accountType === 'student' && inviteRole === 'student') return true;
      if (accountType === 'professor' && inviteRole === 'professor') return true;
      if (accountType === 'recruiter' && inviteRole === 'recruiter') return true;
      return false;
    }

    // Legacy role codes
    const roleMapping: Record<string, string> = {
      'professor': 'professor',
      'recruiter': 'recruiter',
    };

    return inviteRole === roleMapping[accountType];
  };

  const getInviteRoleLabel = (): string => {
    const normalizedCode = inviteCode.trim().toUpperCase();
    if (normalizedCode.startsWith('MGR')) return 'Manager';
    if (normalizedCode.startsWith('REP')) return 'Sales Rep';
    if (inviteRole === 'professor') return 'Professor';
    if (inviteRole === 'recruiter') return 'Recruiter';
    if (inviteRole === 'student') return 'Student';
    return inviteRole || 'selected';
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    const normalizedEmail = email.trim().toLowerCase();

    try {
      emailSchema.parse(normalizedEmail);
    } catch (e) {
      if (e instanceof z.ZodError) {
        newErrors.email = e.issues[0].message;
      }
    }

    // Every signup must have an OTP-verified email
    if (mode === 'signUp' && !studentEmailVerified) {
      newErrors.email = 'Please verify your email first';
    }

    // If NOT a student during signup, reject .edu emails (preserves student-only school flow)
    if (mode === 'signUp' && !isStudent && normalizedEmail.endsWith('.edu') && !inviteCodeValid) {
      newErrors.email = 'Please check "I\'m a student" to use a .edu email';
    }

    if (mode !== 'resetPassword') {
      try {
        passwordSchema.parse(password);
      } catch (e) {
        if (e instanceof z.ZodError) {
          newErrors.password = e.issues[0].message;
        }
      }
    }

    if (mode === 'signUp') {
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      // For restricted account types, invite code must be valid and role must match
      if (RESTRICTED_ACCOUNT_TYPES.includes(accountType)) {
        if (!inviteCodeValid) {
          newErrors.inviteCode = 'Please enter a valid invite code';
        } else if (!isInviteCodeRoleValid()) {
          newErrors.inviteCode = `This code is not valid for ${selectedRoleLabel || accountType} accounts`;
        }
      }
      // If invite code is entered for non-restricted types, it must be valid
      else if (showInviteCode && inviteCode && !inviteCodeValid) {
        newErrors.inviteCode = 'Please enter a valid invite code';
      }
      
      // Terms must be accepted for signup
      if (!termsAccepted) {
        newErrors.terms = 'You must accept the Terms of Service and Privacy Policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validateForm()) return;

    if (resetCooldownSecondsLeft > 0) {
      toast({
        title: 'Please wait',
        description: `You can request another reset email in ${resetCooldownSecondsLeft}s.`,
      });
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const redirectUrl = `${window.location.origin}/auth`;

      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      setResetEmail(normalizedEmail);
      setResetCooldownEndsAt(Date.now() + 60_000);
      setMode('enterCode');
      toast({
        title: 'Check your email',
        description: 'We sent you a 6-digit code. Enter it below to reset your password.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send reset email',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (!resetCode || resetCode.length < 6) {
      setErrors({ resetCode: 'Please enter the 6-digit code from your email' });
      return;
    }

    setResetCodeVerifying(true);
    setErrors({});

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: resetEmail,
        token: resetCode,
        type: 'recovery',
      });

      if (error) throw error;

      setMode('setPassword');
      setResetCode('');
      toast({
        title: 'Code verified!',
        description: 'You can now set your new password.',
      });
    } catch (error) {
      setErrors({ resetCode: 'Invalid or expired code. Please try again or request a new one.' });
      toast({
        title: 'Invalid code',
        description: 'The code is invalid or expired. Please request a new one.',
        variant: 'destructive',
      });
    } finally {
      setResetCodeVerifying(false);
    }
  };

  const handleSetNewPassword = async () => {
    // Validate password
    try {
      passwordSchema.parse(password);
    } catch (e) {
      if (e instanceof z.ZodError) {
        setErrors({ password: e.issues[0].message });
        return;
      }
    }
    
    if (password !== confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      toast({
        title: 'Password updated!',
        description: 'Your password has been successfully changed.',
      });
      
      // Sign out and redirect to sign in
      await supabase.auth.signOut();
      setMode('signIn');
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'resetPassword') {
      return handleResetPassword();
    }

    if (!validateForm()) return;

    const normalizedEmail = email.trim().toLowerCase();

    setLoading(true);

    try {
      if (mode === 'signUp') {
        // For license-kind codes, role grant happens server-side via redeem RPC.
        // For legacy role codes (professor/recruiter/judge), pass the role to signUp.
        const isLicenseCode = inviteKind === 'license';
        const roleForSignup = inviteCodeValid && inviteRole && !isLicenseCode ? inviteRole : undefined;
        // A professor license code should provision a professor-style account, not a student
        const accountTypeForSignup = isLicenseCode && inviteRole === 'professor'
          ? 'professor'
          : isLicenseCode && inviteRole === 'recruiter'
            ? 'recruiter'
            : accountType || undefined;

        // Stash license invite code BEFORE signUp so the SIGNED_IN listener
        // (fired during signUp on auto-confirm) can redeem it.
        if (isLicenseCode && inviteCode) {
          const upper = inviteCode.trim().toUpperCase();
          try { sessionStorage.setItem('pending_invite_code', upper); } catch {}
          try { localStorage.setItem('pending_invite_code', upper); } catch {}
        }

        const { error } = await signUp(
          normalizedEmail,
          password,
          undefined,
          roleForSignup,
          accountTypeForSignup
        );

        if (error) {
          // Clear the stashed code if signup failed
          if (isLicenseCode) {
            try { sessionStorage.removeItem('pending_invite_code'); } catch {}
            try { localStorage.removeItem('pending_invite_code'); } catch {}
          }
          if (error.message.includes('already registered')) {
            toast({
              title: 'Account exists',
              description: 'This email is already registered. Please sign in instead.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          if (inviteCodeValid && inviteCode && !isLicenseCode) {
            // Consume legacy role-based invite code immediately
            try {
              await supabase.functions.invoke('consume-invite-code', {
                body: { code: inviteCode, email: normalizedEmail },
              });
            } catch (consumeError) {
              console.error('Error consuming invite code:', consumeError);
            }
          }

          // Store terms acceptance timestamp
          // Note: This will be saved when the user confirms their email and the profile is created
          localStorage.setItem('pending_terms_accepted_at', new Date().toISOString());

          // Store student email and detected school for profile update after confirmation
          if (isStudent && normalizedEmail.endsWith('.edu')) {
            localStorage.setItem('pending_student_verification', normalizedEmail);
            if (detectedSchool) {
              localStorage.setItem('pending_student_school', detectedSchool);
            }
          }

          // Email was OTP-verified pre-signup — auto-confirm the auth user and sign them in
          let autoConfirmed = false;
          try {
            const { data: confirmData } = await supabase.functions.invoke('confirm-presignup-user', {
              body: { email: normalizedEmail },
            });
            autoConfirmed = !!confirmData?.success;
          } catch (confirmErr) {
            console.error('Error auto-confirming user:', confirmErr);
          }

          if (autoConfirmed) {
            const { error: signInErr } = await signIn(normalizedEmail, password);
            if (!signInErr) {
              toast({
                title: 'Account created!',
                description: inviteRole
                  ? `Welcome — you're set up as a ${inviteRole}.`
                  : 'Welcome to bigE Sales Coach.',
              });
              navigate('/onboarding');
              return;
            }
          }

          // Fallback (auto-confirm failed): old email-link confirmation flow
          const description = isStudent
            ? 'Please check your school email to confirm your account. After confirming, we\'ll verify your student status.'
            : inviteRole
              ? `You've been registered as a ${inviteRole}. Please check your email to confirm your account.`
              : 'Please check your email to confirm your account.';

          toast({
            title: 'Account created!',
            description,
          });

          setPendingVerificationEmail(normalizedEmail);
          setMode('verifyEmail');
        }
      } else {
        const { error } = await signIn(normalizedEmail, password);
        if (error) {
          const msg = error.message || '';
          if (/email not confirmed/i.test(msg)) {
            setPendingVerificationEmail(normalizedEmail);
            setMode('verifyEmail');
            toast({
              title: 'Email not confirmed',
              description: 'Please confirm your email before signing in. We can resend the link.',
            });
          } else if (msg.includes('Invalid login')) {
            toast({
              title: 'Invalid credentials',
              description: 'Please check your email and password.',
              variant: 'destructive',
            });
          } else {
            throw error;
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'selectAccountType': return 'Choose Account Type';
      case 'enterInviteCode': return `${selectedRoleLabel || (accountType === 'professor' ? 'Professor' : accountType === 'recruiter' ? 'Recruiter' : 'Student')} Verification`;
      case 'signUp': 
        if (selectedRoleLabel) return `Create ${selectedRoleLabel} Account`;
        if (accountType === 'student') return 'Create Student Account';
        if (accountType === 'professor') return 'Create Professor Account';
        if (accountType === 'recruiter') return 'Create Recruiter Account';
        return 'Create Account';
      case 'resetPassword': return t('auth.resetPassword');
      case 'resetSent': return t('auth.resetSent');
      case 'enterCode': return 'Enter Reset Code';
      case 'setPassword': return 'Set New Password';
      case 'verifyEmail': return 'Confirm your email';
      default: return t('auth.welcomeBack');
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'selectAccountType': return 'Select the type of account that best fits you';
      case 'enterInviteCode': return `Enter your invite code to create a ${selectedRoleLabel || accountType} account`;
      case 'signUp': 
        if (accountType === 'student' && !selectedRoleLabel) return 'Verify your school email and create your password';
        return 'Enter your details to get started';
      case 'resetPassword': return t('auth.enterEmailReset');
      case 'resetSent': return t('auth.resetSentDesc');
      case 'enterCode': return `Enter the 6-digit code we sent to ${resetEmail}`;
      case 'setPassword': return 'Enter your new password below';
      case 'verifyEmail': return `We sent a confirmation link to ${pendingVerificationEmail}`;
      default: return t('auth.signInToContinue');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'professor': return 'border-amber-500/50 text-amber-500 bg-amber-500/10';
      case 'judge': return 'border-emerald-500/50 text-emerald-500 bg-emerald-500/10';
      case 'recruiter': return 'border-cyan-500/50 text-cyan-500 bg-cyan-500/10';
      case 'admin': return 'border-rose-500/50 text-rose-500 bg-rose-500/10';
      default: return 'border-primary/50 text-primary bg-primary/10';
    }
  };

  const handleResendVerification = async () => {
    if (!pendingVerificationEmail) return;
    setResendingVerification(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingVerificationEmail,
        options: { emailRedirectTo: `${window.location.origin}/` },
      });
      if (error) throw error;
      toast({
        title: 'Email sent',
        description: `We re-sent the confirmation link to ${pendingVerificationEmail}. Check your spam folder if it doesn't arrive.`,
      });
    } catch (err) {
      toast({
        title: 'Could not resend',
        description: err instanceof Error ? err.message : 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSendStudentVerification = async () => {
    const emailToVerify = email.trim().toLowerCase();
    // The .edu / school-approval flow only applies for student signups WITHOUT an invite code.
    const isStudentSchoolFlow = isStudent && !inviteCodeValid;

    if (!emailToVerify) {
      setErrors(prev => ({ ...prev, email: isStudentSchoolFlow ? 'Please enter your school email' : 'Please enter your email' }));
      return;
    }

    // Basic email format check for all flows
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToVerify)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }

    if (isStudentSchoolFlow && !emailToVerify.endsWith('.edu')) {
      setErrors(prev => ({ ...prev, email: 'Please use a valid .edu email address' }));
      return;
    }

    setStudentEmailSending(true);
    setErrors(prev => ({ ...prev, email: undefined }));

    try {
      const { data, error } = await supabase.functions.invoke('send-presignup-verification', {
        body: { email: emailToVerify, skip_school_check: !isStudentSchoolFlow },
      });

      // Handle edge function errors - the error object may contain the response
      if (error) {
        // Try to extract error message from the response
        const errorMessage = data?.error || error.message || 'Failed to send verification code';
        throw new Error(errorMessage);
      }

      if (data?.success) {
        setStudentEmailSent(true);
        setSchoolPendingReview(false);
        toast({
          title: 'Verification code sent!',
          description: `Check ${emailToVerify} for the 6-digit code. Check your spam folder if it doesn't arrive.`,
        });
      } else if (data?.pending_review) {
        setSchoolPendingReview(true);
        toast({
          title: 'School pending review',
          description: data.message || "Your school hasn't been approved yet. We'll notify you when it's approved.",
        });
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Failed to send verification code');
      }
    } catch (err: unknown) {
      console.error('Error sending verification:', err);
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setErrors(prev => ({ ...prev, email: message }));
      toast({
        title: 'Verification failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setStudentEmailSending(false);
    }
  };

  const handleVerifyCode = async () => {
    const emailToVerify = email.trim().toLowerCase();
    const codeToVerify = verificationCode.trim();

    if (!codeToVerify || codeToVerify.length !== 6) {
      setErrors(prev => ({ ...prev, email: 'Please enter the 6-digit verification code' }));
      return;
    }

    setVerificationCodeVerifying(true);
    setErrors(prev => ({ ...prev, email: undefined }));

    try {
      const { data, error } = await supabase.functions.invoke('verify-presignup-code', {
        body: { email: emailToVerify, code: codeToVerify },
      });

      if (error) throw error;

      if (data.valid) {
        setStudentEmailVerified(true);
        if (data.school) {
          setDetectedSchool(data.school);
        }
        toast({
          title: 'Email verified!',
          description: data.school 
            ? `Verified as a student at ${data.school}!` 
            : 'Your school email has been verified. You can now create your account.',
        });
      } else {
        setErrors(prev => ({ ...prev, email: data.error || 'Invalid verification code' }));
      }
    } catch (err: unknown) {
      console.error('Error verifying code:', err);
      setErrors(prev => ({ ...prev, email: err instanceof Error ? err.message : 'Something went wrong' }));
    } finally {
      setVerificationCodeVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-dark flex flex-col relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid opacity-[0.02]" />
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-primary/15 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-accent/15 rounded-full blur-[128px]" />
      </div>

      {/* Header */}
      <header className="p-6 relative z-10">
        <Button
          variant="glass"
          onClick={() => {
            if (mode === 'signUp') {
              // For restricted account types, go back to invite code entry
              if (RESTRICTED_ACCOUNT_TYPES.includes(accountType)) {
                setMode('enterInviteCode');
              } else {
                // Go back to account type selection
                setMode('selectAccountType');
                setAccountType(null);
              }
            } else if (mode === 'enterInviteCode') {
              // Go back to account type selection and reset invite code state
              setMode('selectAccountType');
              setAccountType(null);
              setInviteCode('');
              setInviteCodeValid(null);
              setInviteRole(null);
              setShowInviteCode(false);
              setErrors({});
            } else if (mode === 'selectAccountType') {
              setMode('signIn');
            } else if (mode === 'resetPassword' || mode === 'resetSent' || mode === 'enterCode') {
              setMode('signIn');
            } else {
              navigate('/');
            }
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-12 relative z-10">
        <Card className="w-full max-w-md p-8 animate-scale-in glow-primary">
          <div className="text-center mb-8">
            <div className="mb-4 flex justify-center"><Logo size="md" /></div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              {getTitle()}
            </h1>
            <p className="text-muted-foreground mt-2">
              {getSubtitle()}
            </p>
          </div>

          {mode === 'verifyEmail' ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground mb-3">
                Click the link in the email to confirm your account, then return here to sign in.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Don't see it? Check your spam folder. Only the most recent confirmation link works.
              </p>
              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                >
                  {resendingVerification ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resending...
                    </>
                  ) : (
                    'Resend confirmation email'
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setMode('signIn')}
                >
                  Back to sign in
                </Button>
              </div>
            </div>
          ) : mode === 'resetSent' ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <p className="text-muted-foreground mb-3">
                {t('auth.resetSentMessage')}
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Don't see it? Check your spam folder. Only the newest reset email link works — avoid requesting multiple emails.
              </p>
              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setMode('resetPassword')}
                  disabled={resetCooldownSecondsLeft > 0}
                >
                  {resetCooldownSecondsLeft > 0
                    ? `Resend in ${resetCooldownSecondsLeft}s`
                    : 'Resend email'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setMode('signIn')}
                >
                  {t('auth.returnToSignIn')}
                </Button>
              </div>
            </div>
          ) : mode === 'enterCode' ? (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="resetCode" className="text-foreground">6-Digit Code</Label>
                <Input
                  id="resetCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setResetCode(value);
                    setErrors(prev => ({ ...prev, resetCode: undefined }));
                  }}
                  placeholder="000000"
                  className={`bg-card/50 border-white/10 focus:border-primary/50 text-center text-2xl tracking-widest font-mono ${errors.resetCode ? 'border-destructive' : ''}`}
                />
                {errors.resetCode && (
                  <p className="text-sm text-destructive">{errors.resetCode}</p>
                )}
              </div>

              <Button
                type="button"
                className="w-full"
                onClick={handleVerifyResetCode}
                disabled={resetCodeVerifying || resetCode.length < 6}
              >
                {resetCodeVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </Button>

              <div className="text-center space-y-2">
                <p className="text-xs text-muted-foreground">
                  Didn't receive the code? Check your spam folder.
                </p>
                <Button
                  type="button"
                  variant="link"
                  className="text-sm"
                  onClick={() => {
                    setResetCode('');
                    setMode('resetPassword');
                  }}
                  disabled={resetCooldownSecondsLeft > 0}
                >
                  {resetCooldownSecondsLeft > 0
                    ? `Resend in ${resetCooldownSecondsLeft}s`
                    : 'Request new code'}
                </Button>
              </div>
            </div>
          ) : mode === 'setPassword' ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSetNewPassword(); }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-foreground">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordRequirements password={password} />
                <PasswordStrengthIndicator password={password} />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmNewPassword" className="text-foreground">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmNewPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Password'
                )}
              </Button>
            </form>
          ) : mode === 'selectAccountType' ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  setAccountType('student');
                  setIsStudent(true);
                  setSelectedRoleLabel('Student');
                  try { sessionStorage.removeItem('pending_user_type'); } catch {}
                  setMode('enterInviteCode');
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-primary/50 bg-card/50 hover:bg-card/80 transition-all text-left group hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center flex-shrink-0 group-hover:from-primary/40 group-hover:to-primary/20 transition-all shadow-lg shadow-primary/20">
                    <Rocket className="w-7 h-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-lg">Student</h3>
                      <Badge variant="outline" className="text-xs border-primary/50 text-primary">Invite Only</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Launch your sales career with AI-powered practice
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAccountType('student');
                  setIsStudent(false);
                  setSelectedRoleLabel('Sales Rep');
                  try { sessionStorage.setItem('pending_user_type', 'sales_rep'); } catch {}
                  setMode('enterInviteCode');
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-emerald-500/50 bg-card/50 hover:bg-card/80 transition-all text-left group hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-emerald-500/40 group-hover:to-emerald-500/20 transition-all shadow-lg shadow-emerald-500/20">
                    <TrendingUp className="w-7 h-7 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-emerald-500 transition-colors text-lg">Sales Rep</h3>
                      <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-500">Invite Only</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Sharpen your pitch and close more deals with AI coaching
                    </p>
                  </div>
                </div>
              </button>

              {/* Professional account type hidden for now */}

              <button
                type="button"
                onClick={() => {
                  setAccountType('professor');
                  setIsStudent(false);
                  setSelectedRoleLabel('Professor');
                  try { sessionStorage.removeItem('pending_user_type'); } catch {}
                  setMode('enterInviteCode'); // Go to invite code entry first
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-amber-500/50 bg-card/50 hover:bg-card/80 transition-all text-left group hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/30 to-amber-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-amber-500/40 group-hover:to-amber-500/20 transition-all shadow-lg shadow-amber-500/20">
                    <Users className="w-7 h-7 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-amber-500 transition-colors text-lg">Professor</h3>
                      <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-500">Invite Only</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Train and track the next generation of sales talent
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAccountType('professor');
                  setIsStudent(false);
                  setSelectedRoleLabel('Manager');
                  try { sessionStorage.setItem('pending_user_type', 'manager'); } catch {}
                  setMode('enterInviteCode');
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-violet-500/50 bg-card/50 hover:bg-card/80 transition-all text-left group hover:scale-[1.02] hover:shadow-lg hover:shadow-violet-500/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/30 to-violet-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-violet-500/40 group-hover:to-violet-500/20 transition-all shadow-lg shadow-violet-500/20">
                    <UserCog className="w-7 h-7 text-violet-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-violet-500 transition-colors text-lg">Manager</h3>
                      <Badge variant="outline" className="text-xs border-violet-500/50 text-violet-500">Invite Only</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Coach your team and track rep performance
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setAccountType('recruiter');
                  setIsStudent(false);
                  setSelectedRoleLabel('Recruiter');
                  try { sessionStorage.removeItem('pending_user_type'); } catch {}
                  setMode('enterInviteCode'); // Go to invite code entry first
                }}
                className="w-full p-4 rounded-xl border-2 border-white/10 hover:border-cyan-500/50 bg-card/50 hover:bg-card/80 transition-all text-left group hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/10"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-cyan-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-cyan-500/40 group-hover:to-cyan-500/20 transition-all shadow-lg shadow-cyan-500/20">
                    <Search className="w-7 h-7 text-cyan-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground group-hover:text-cyan-500 transition-colors text-lg">Recruiter</h3>
                      <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-500">Invite Only</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Discover and connect with top sales performers
                    </p>
                  </div>
                </div>
              </button>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setMode('signIn')}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors block w-full text-center"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          ) : mode === 'enterInviteCode' ? (
            <div className="space-y-6">
              {/* Account Type Indicator */}
              <div className="flex items-center justify-center gap-3 p-4 rounded-xl bg-card/50 border border-white/10">
                {selectedRoleLabel === 'Sales Rep' ? (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-emerald-500/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-500" />
                  </div>
                ) : selectedRoleLabel === 'Manager' ? (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/30 to-violet-500/10 flex items-center justify-center">
                    <UserCog className="w-6 h-6 text-violet-500" />
                  </div>
                ) : accountType === 'professor' ? (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/30 to-amber-500/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-amber-500" />
                  </div>
                ) : accountType === 'recruiter' ? (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-cyan-500/10 flex items-center justify-center">
                    <Search className="w-6 h-6 text-cyan-500" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div>
                  <h3 className={`font-semibold ${selectedRoleLabel === 'Sales Rep' ? 'text-emerald-500' : selectedRoleLabel === 'Manager' ? 'text-violet-500' : accountType === 'professor' ? 'text-amber-500' : accountType === 'recruiter' ? 'text-cyan-500' : 'text-primary'}`}>
                    {selectedRoleLabel || (accountType ? accountType.charAt(0).toUpperCase() + accountType.slice(1) : 'Student')} Account
                  </h3>
                  <p className="text-xs text-muted-foreground">Requires invite code</p>
                </div>
              </div>

              {/* Invite Code Input */}
              <div className="space-y-3">
                <Label htmlFor="inviteCodeRestricted" className="text-foreground flex items-center gap-2">
                  <Ticket className="w-4 h-4" />
                  Invite Code
                </Label>
                <div className="relative">
                  <Input
                    id="inviteCodeRestricted"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setErrors(prev => ({ ...prev, inviteCode: undefined }));
                    }}
                    placeholder="Enter your invite code"
                    className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 uppercase text-center text-lg tracking-wider ${
                      errors.inviteCode ? 'border-destructive' : 
                      inviteCodeValid === true && isInviteCodeRoleValid() ? 'border-emerald-500' : 
                      inviteCodeValid === false || (inviteCodeValid === true && !isInviteCodeRoleValid()) ? 'border-destructive' : ''
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {inviteCodeValidating ? (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    ) : inviteCodeValid === true && isInviteCodeRoleValid() ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : inviteCodeValid === false || (inviteCodeValid === true && !isInviteCodeRoleValid()) ? (
                      <X className="w-4 h-4 text-destructive" />
                    ) : null}
                  </div>
                </div>
                
                {/* Status Messages */}
                {inviteCodeValid === true && isInviteCodeRoleValid() && (
                  <InviteSummary
                    kind={inviteKind}
                    role={getInviteRoleLabel()}
                    licenseName={inviteLicenseName}
                    schoolName={inviteSchoolName}
                    licenseType={inviteLicenseType}
                    seatsAvailable={inviteSeatsAvailable}
                    maxSeats={inviteMaxSeats}
                    expiresAt={inviteExpiresAt}
                  />
                )}
                {inviteCodeValid === true && !isInviteCodeRoleValid() && (
                  <p className="text-sm text-destructive">
                    This code is for {getInviteRoleLabel()} accounts, not {selectedRoleLabel || accountType} accounts
                  </p>
                )}
                {inviteCodeValid === false && (
                  <p className="text-sm text-destructive">{errors.inviteCode || 'Invalid invite code'}</p>
                )}
              </div>

              {/* Continue Button */}
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  setShowInviteCode(true); // Mark invite code as active for signup
                  setMode('signUp');
                }}
                disabled={!inviteCodeValid || !isInviteCodeRoleValid() || inviteCodeValidating}
              >
                Continue to Create Account
              </Button>

              {/* Help Text */}
              <div className="text-center space-y-2">
                <p className="text-xs text-muted-foreground">
                  Don't have an invite code? Contact your institution administrator.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setMode('selectAccountType');
                    setAccountType(null);
                    setInviteCode('');
                    setInviteCodeValid(null);
                    setInviteRole(null);
                  }}
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Choose a different account type
                </button>
              </div>
            </div>
          ) : (
            <>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'signUp' && (
                  <>
                    {/* Invite Code Section - only show for non-restricted account types */}
                    {!RESTRICTED_ACCOUNT_TYPES.includes(accountType) && (
                      <div className="space-y-2">
                        {!showInviteCode ? (
                          <button
                            type="button"
                            onClick={() => setShowInviteCode(true)}
                            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                          >
                            <Ticket className="w-4 h-4" />
                            {t('auth.haveInviteCode')}
                          </button>
                        ) : (
                          <>
                            <div className="flex items-center justify-between">
                              <Label htmlFor="inviteCode" className="text-foreground flex items-center gap-2">
                                <Ticket className="w-4 h-4" />
                                {t('auth.inviteCode')}
                                <span className="text-xs text-muted-foreground">({t('auth.optional')})</span>
                              </Label>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowInviteCode(false);
                                  setInviteCode('');
                                  setInviteCodeValid(null);
                                  setInviteRole(null);
                                  setErrors(prev => ({ ...prev, inviteCode: undefined }));
                                }}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Hide
                              </button>
                            </div>
                            <div className="relative">
                              <Input
                                id="inviteCode"
                                type="text"
                                value={inviteCode}
                                onChange={(e) => {
                                  setInviteCode(e.target.value.toUpperCase());
                                  setErrors(prev => ({ ...prev, inviteCode: undefined }));
                                }}
                                placeholder="Enter code (e.g., PROF2024)"
                                className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 uppercase ${
                                  errors.inviteCode ? 'border-destructive' : 
                                  inviteCodeValid === true ? 'border-emerald-500' : 
                                  inviteCodeValid === false ? 'border-destructive' : ''
                                }`}
                              />
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {inviteCodeValidating ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                ) : inviteCodeValid === true ? (
                                  <Check className="w-4 h-4 text-emerald-500" />
                                ) : inviteCodeValid === false ? (
                                  <X className="w-4 h-4 text-destructive" />
                                ) : null}
                              </div>
                            </div>
                            {inviteCodeValid && inviteRole && inviteKind === 'license' && (
                              <InviteSummary
                                kind={inviteKind}
                                role={inviteRole}
                                licenseName={inviteLicenseName}
                                schoolName={inviteSchoolName}
                                licenseType={inviteLicenseType}
                                seatsAvailable={inviteSeatsAvailable}
                                maxSeats={inviteMaxSeats}
                                expiresAt={inviteExpiresAt}
                              />
                            )}
                            {inviteCodeValid && inviteRole && inviteKind !== 'license' && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">Registering as:</span>
                                <Badge className={`capitalize ${getRoleBadgeColor(inviteRole)}`}>
                                  {inviteRole}
                                </Badge>
                              </div>
                            )}
                            {errors.inviteCode && (
                              <p className="text-sm text-destructive">{errors.inviteCode}</p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Account Type Badge - show which account type was selected */}
                    {accountType && (
                      <div className={`flex items-center justify-between p-3 rounded-lg border ${
                        selectedRoleLabel === 'Manager' ? 'border-violet-500/30 bg-violet-500/5' :
                        selectedRoleLabel === 'Sales Rep' ? 'border-emerald-500/30 bg-emerald-500/5' :
                        accountType === 'professor' ? 'border-amber-500/30 bg-amber-500/5' :
                        accountType === 'recruiter' ? 'border-cyan-500/30 bg-cyan-500/5' :
                        'border-primary/30 bg-primary/5'
                      }`}>
                        <div className="flex items-center gap-2">
                          {selectedRoleLabel === 'Manager' ? (
                            <Users className="w-4 h-4 text-violet-500" />
                          ) : selectedRoleLabel === 'Sales Rep' ? (
                            <Briefcase className="w-4 h-4 text-emerald-500" />
                          ) : accountType === 'student' ? (
                            <GraduationCap className="w-4 h-4 text-primary" />
                          ) : accountType === 'professor' ? (
                            <Users className="w-4 h-4 text-amber-500" />
                          ) : accountType === 'recruiter' ? (
                            <Search className="w-4 h-4 text-cyan-500" />
                          ) : (
                            <Briefcase className="w-4 h-4 text-accent" />
                          )}
                          <span className={`text-sm font-medium ${!selectedRoleLabel ? 'capitalize' : ''} ${
                            selectedRoleLabel === 'Manager' ? 'text-violet-500' :
                            selectedRoleLabel === 'Sales Rep' ? 'text-emerald-500' :
                            accountType === 'professor' ? 'text-amber-500' :
                            accountType === 'recruiter' ? 'text-cyan-500' :
                            'text-foreground'
                          }`}>{selectedRoleLabel || accountType} Account</span>
                        </div>
                        {accountType === 'student' && !selectedRoleLabel && studentEmailVerified && (
                          <Badge className="border-emerald-500/50 text-emerald-500 bg-emerald-500/10">
                            <Check className="w-3 h-3 mr-1" />
                            {detectedSchool || 'Verified'}
                          </Badge>
                        )}
                        {(RESTRICTED_ACCOUNT_TYPES.includes(accountType) || selectedRoleLabel) && inviteCodeValid && (
                          <Badge className="border-emerald-500/50 text-emerald-500 bg-emerald-500/10">
                            <Check className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    {mode === 'signUp' && isStudent && !inviteCodeValid ? 'School Email' : 'Email'}
                  </Label>
                  
                  {/* All signup flows require OTP email verification. Student .edu flow includes school approval. */}
                  {mode === 'signUp' ? (
                    studentEmailVerified ? (
                      // Email is verified - show read-only email with checkmark and school name
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <div className="flex flex-col">
                            <span className="text-sm text-emerald-500">{email}</span>
                            {detectedSchool && (
                              <span className="text-xs text-emerald-400/80 flex items-center gap-1">
                                <GraduationCap className="w-3 h-3" />
                                {detectedSchool}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : studentEmailSent ? (
                      // Code sent - show code input
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/30 rounded-md">
                          <Mail className="w-4 h-4 text-primary" />
                          <span className="text-sm text-foreground">{email}</span>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            placeholder="Enter 6-digit code"
                            value={verificationCode}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setVerificationCode(val);
                              setErrors(prev => ({ ...prev, email: undefined }));
                            }}
                            maxLength={6}
                            className="bg-card/50 border-white/10 focus:border-primary/50 flex-1 text-center text-lg tracking-widest"
                          />
                          <Button
                            type="button"
                            onClick={handleVerifyCode}
                            disabled={verificationCodeVerifying || verificationCode.length !== 6}
                          >
                            {verificationCodeVerifying ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Verify'
                            )}
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setStudentEmailSent(false);
                            setVerificationCode('');
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Change email or resend code
                        </button>
                      </div>
                    ) : schoolPendingReview ? (
                      // School pending review state
                      <div className="space-y-3">
                        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-foreground">School Pending Review</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Your school hasn't been approved yet. We've submitted a request for review and will notify you at <span className="font-medium text-foreground">{email}</span> when it's approved.
                              </p>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSchoolPendingReview(false);
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Try a different email
                        </button>
                      </div>
                    ) : (
                      // Initial state - show email input with send button
                      <div className="flex gap-2">
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors(prev => ({ ...prev, email: undefined }));
                          }}
                          placeholder={isStudent && !inviteCodeValid ? 'you@university.edu' : 'you@example.com'}
                          className={`bg-card/50 border-white/10 focus:border-primary/50 flex-1 ${errors.email ? 'border-destructive' : ''}`}
                        />
                        <Button
                          type="button"
                          onClick={handleSendStudentVerification}
                          disabled={studentEmailSending || !email.trim()}
                        >
                          {studentEmailSending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-3 h-3 mr-1" />
                              Verify
                            </>
                          )}
                        </Button>
                      </div>
                    )
                  ) : (
                    // Non-student: Regular email input
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={`bg-card/50 border-white/10 focus:border-primary/50 ${errors.email ? 'border-destructive' : ''}`}
                    />
                  )}
                  
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email}</p>
                  )}
                </div>

                {mode !== 'resetPassword' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-foreground">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                          className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {mode === 'signUp' && (
                        <>
                          <PasswordRequirements password={password} />
                          <PasswordStrengthIndicator password={password} />
                        </>
                      )}
                      {errors.password && (
                        <p className="text-sm text-destructive">{errors.password}</p>
                      )}
                    </div>

                    {mode === 'signUp' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword" className="text-foreground">Confirm Password</Label>
                          <div className="relative">
                            <Input
                              id="confirmPassword"
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="••••••••"
                              autoComplete="new-password"
                              className={`bg-card/50 border-white/10 focus:border-primary/50 pr-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          {errors.confirmPassword && (
                            <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                          )}
                        </div>

                        {/* Terms and Conditions Checkbox */}
                        <div className="space-y-2">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="terms"
                              checked={termsAccepted}
                              onCheckedChange={(checked) => {
                                setTermsAccepted(checked === true);
                                setErrors(prev => ({ ...prev, terms: undefined }));
                              }}
                              className={errors.terms ? 'border-destructive' : ''}
                            />
                            <label
                              htmlFor="terms"
                              className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                            >
                              I agree to the{' '}
                              <a
                                href="/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 underline transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Terms of Service
                              </a>{' '}
                              and{' '}
                              <a
                                href="/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 underline transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Privacy Policy
                              </a>
                            </label>
                          </div>
                          {errors.terms && (
                            <p className="text-sm text-destructive">{errors.terms}</p>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                <Button
                  type="submit"
                  variant="glass"
                  className="w-full bg-primary/20 hover:bg-primary/30 text-primary border-primary/30 hover:border-primary/50 shadow-lg shadow-primary/10 hover:shadow-primary/20"
                  disabled={
                    loading ||
                    (mode === 'resetPassword' && resetCooldownSecondsLeft > 0)
                  }
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : mode === 'signUp' ? (
                    'Create Account'
                  ) : mode === 'resetPassword' ? (
                    resetCooldownSecondsLeft > 0
                      ? `Resend in ${resetCooldownSecondsLeft}s`
                      : 'Send Reset Link'
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="mt-6 space-y-3">
                {mode === 'signIn' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('resetPassword');
                      setErrors({});
                    }}
                    className="text-sm text-primary hover:text-primary/80 transition-colors block w-full text-center"
                  >
                    Forgot your password?
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    if (mode === 'signUp') {
                      setMode('signIn');
                      setAccountType(null);
                    } else if (mode === 'signIn') {
                      setMode('selectAccountType');
                    } else {
                      setMode('signIn');
                    }
                    setErrors({});
                    setShowInviteCode(false);
                    setInviteCode('');
                    setInviteCodeValid(null);
                    setInviteRole(null);
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors block w-full text-center"
                >
                  {mode === 'signUp' 
                    ? 'Already have an account? Sign in' 
                    : mode === 'resetPassword'
                      ? 'Remember your password? Sign in'
                      : "Don't have an account? Sign up"
                  }
                </button>
              </div>
            </>
          )}
        </Card>
      </main>
    </div>
  );
};

export default Auth;
